import React, { useState, useRef } from 'react';
import { Button } from './Common';
import ImageEditor from './ImageEditor';

export function parseSteps(value) {
  if (!value) return [{ description: '' }];
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed.length > 0 ? parsed : [{ description: '' }];
    } catch {}
    const lines = value.split('\n').filter(l => l.trim());
    if (lines.length === 0) return [{ description: '' }];
    return lines.map(l => ({ description: l.replace(/^\d+[\.\)]\s*/, '') }));
  }
  if (Array.isArray(value)) return value.length > 0 ? value : [{ description: '' }];
  return [{ description: '' }];
}

export default function StepsToReproduce({ value, onChange, evidence, engagementId, findingId, apiFetch, onEvidenceChange }) {
  const [steps, setSteps] = useState(() => parseSteps(value));
  const [uploading, setUploading] = useState(null);
  const [editingImage, setEditingImage] = useState(null);
  const fileRefs = useRef({});

  const update = (newSteps) => {
    setSteps(newSteps);
    onChange(JSON.stringify(newSteps));
  };

  const updateStep = (idx, desc) => {
    update(steps.map((s, i) => i === idx ? { ...s, description: desc } : s));
  };

  const addStep = (afterIdx) => {
    const next = [...steps];
    next.splice(afterIdx + 1, 0, { description: '' });
    update(next);
  };

  const removeStep = (idx) => {
    if (steps.length <= 1) { update([{ description: '' }]); return; }
    update(steps.filter((_, i) => i !== idx));
  };

  const moveStep = (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= steps.length) return;
    const next = [...steps];
    [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
    update(next);
  };

  const stepEvidence = (stepIdx) => (evidence || []).filter(e => e.step_index === stepIdx);

  const uploadForStep = async (stepIdx, file) => {
    if (!findingId || !file) return;
    setUploading(stepIdx);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('stepIndex', String(stepIdx));
      fd.append('caption', `Step ${stepIdx + 1}`);
      const res = await apiFetch(`/findings/${engagementId}/${findingId}/evidence`, { method: 'POST', body: fd, isFormData: true });
      if (res.ok && onEvidenceChange) onEvidenceChange();
    } catch { alert('Failed to upload screenshot'); }
    setUploading(null);
  };

  const handlePaste = (e, stepIdx) => {
    if (!findingId) return;

    // Check clipboardData.items for image file
    const items = e.clipboardData?.items;
    if (items) {
      for (let i = 0; i < items.length; i++) {
        if (items[i].kind === 'file' && items[i].type.startsWith('image/')) {
          e.preventDefault();
          const blob = items[i].getAsFile();
          if (blob) {
            const file = new File([blob], `paste-step${stepIdx + 1}-${Date.now()}.png`, { type: blob.type });
            uploadForStep(stepIdx, file);
          }
          return;
        }
      }
    }

    // Fallback: check clipboardData.files
    const files = e.clipboardData?.files;
    if (files && files.length > 0) {
      for (let i = 0; i < files.length; i++) {
        if (files[i].type.startsWith('image/')) {
          e.preventDefault();
          uploadForStep(stepIdx, files[i]);
          return;
        }
      }
    }

    // Fallback: extract image from HTML paste (e.g., copy from Word)
    const html = e.clipboardData?.getData('text/html');
    if (html) {
      const imgMatch = html.match(/src="data:image\/[^"]+"/);
      if (imgMatch) {
        e.preventDefault();
        const dataUri = imgMatch[0].slice(5, -1);
        const byteString = atob(dataUri.split(',')[1]);
        const mimeMatch = dataUri.match(/data:(image\/\w+)/);
        const mime = mimeMatch ? mimeMatch[1] : 'image/png';
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let j = 0; j < byteString.length; j++) ia[j] = byteString.charCodeAt(j);
        const blob = new Blob([ab], { type: mime });
        const file = new File([blob], `paste-word-step${stepIdx + 1}-${Date.now()}.png`, { type: mime });
        uploadForStep(stepIdx, file);
        return;
      }
    }
  };

  const deleteEvidence = async (evId) => {
    if (!window.confirm('Delete this screenshot?')) return;
    await apiFetch(`/findings/${engagementId}/${findingId}/evidence/${evId}`, { method: 'DELETE' });
    if (onEvidenceChange) onEvidenceChange();
  };

  const openEditor = (ev) => {
    const url = `/api/findings/${engagementId}/${findingId}/evidence/${ev.id}/file?token=${sessionStorage.getItem('token')}`;
    setEditingImage({ url, evId: ev.id });
  };

  const handleEditorSave = async (blob) => {
    if (!editingImage) return;
    const fd = new FormData();
    fd.append('file', new File([blob], `edited-${Date.now()}.png`, { type: 'image/png' }));
    fd.append('stepIndex', String(evidence.find(e => e.id === editingImage.evId)?.step_index ?? ''));
    fd.append('caption', evidence.find(e => e.id === editingImage.evId)?.caption || '');
    // Upload as new, then delete old
    const res = await apiFetch(`/findings/${engagementId}/${findingId}/evidence`, { method: 'POST', body: fd, isFormData: true });
    if (res.ok) {
      await apiFetch(`/findings/${engagementId}/${findingId}/evidence/${editingImage.evId}`, { method: 'DELETE' });
      if (onEvidenceChange) onEvidenceChange();
    }
    setEditingImage(null);
  };

  const token = sessionStorage.getItem('token');

  return (
    <div style={{ margin: '16px 0' }}>
      {editingImage && (
        <ImageEditor
          imageUrl={editingImage.url}
          onSave={handleEditorSave}
          onCancel={() => setEditingImage(null)}
        />
      )}
      <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 8, color: 'var(--text-secondary)' }}>
        Steps to Reproduce
      </label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {steps.map((step, idx) => (
          <div key={idx} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 10, background: 'var(--bg-input)' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--primary)', minWidth: 28, paddingTop: 8 }}>{idx + 1}.</span>
              <textarea
                value={step.description}
                onChange={e => updateStep(idx, e.target.value)}
                onPaste={e => handlePaste(e, idx)}
                placeholder={`Describe step ${idx + 1}... (paste screenshot with Ctrl+V)`}
                rows={2}
                style={{
                  flex: 1, padding: '8px 10px', fontSize: 13, border: '1px solid var(--border)',
                  borderRadius: 6, background: 'var(--bg-card)', color: 'var(--text)',
                  fontFamily: 'inherit', resize: 'vertical', minHeight: 40
                }}
              />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, paddingTop: 4 }}>
                <button onClick={() => moveStep(idx, -1)} disabled={idx === 0} title="Move up"
                  style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 4, cursor: idx === 0 ? 'default' : 'pointer', padding: '2px 6px', fontSize: 11, color: 'var(--text-secondary)', opacity: idx === 0 ? 0.3 : 1 }}>▲</button>
                <button onClick={() => moveStep(idx, 1)} disabled={idx === steps.length - 1} title="Move down"
                  style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 4, cursor: idx === steps.length - 1 ? 'default' : 'pointer', padding: '2px 6px', fontSize: 11, color: 'var(--text-secondary)', opacity: idx === steps.length - 1 ? 0.3 : 1 }}>▼</button>
                <button onClick={() => removeStep(idx)} title="Remove step"
                  style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 4, cursor: 'pointer', padding: '2px 6px', fontSize: 11, color: '#ef4444' }}>✕</button>
              </div>
            </div>

            {/* Per-step screenshots */}
            {findingId && (
              <div style={{ marginTop: 8, paddingLeft: 28 }}>
                {stepEvidence(idx).length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                    {stepEvidence(idx).map(ev => (
                      <div key={ev.id} style={{ position: 'relative', borderRadius: 6, overflow: 'hidden', border: '1px solid var(--border)' }}>
                        <img
                          src={`/api/findings/${engagementId}/${findingId}/evidence/${ev.id}/file?token=${token}`}
                          alt={ev.caption || ev.file_name}
                          style={{ maxHeight: 120, maxWidth: 200, display: 'block', cursor: 'pointer' }}
                          onClick={() => window.open(`/api/findings/${engagementId}/${findingId}/evidence/${ev.id}/file?token=${token}`, '_blank')}
                          title="Click to view full size"
                        />
                        <div style={{ position: 'absolute', top: 2, right: 2, display: 'flex', gap: 2 }}>
                          <button onClick={() => openEditor(ev)} title="Edit"
                            style={{ background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer', fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✎</button>
                          <button onClick={() => deleteEvidence(ev.id)} title="Remove"
                            style={{ background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer', fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <label style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px',
                    background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 6,
                    cursor: uploading === idx ? 'wait' : 'pointer', fontSize: 11, color: 'var(--text-secondary)'
                  }}>
                    {uploading === idx ? '⏳' : '📎'} Upload
                    <input type="file" accept="image/*,.pdf" style={{ display: 'none' }}
                      ref={el => fileRefs.current[idx] = el}
                      onChange={e => { if (e.target.files[0]) uploadForStep(idx, e.target.files[0]); e.target.value = ''; }}
                      disabled={uploading === idx} />
                  </label>
                  <span style={{ fontSize: 10, color: 'var(--text-secondary)' }}>or paste with Ctrl+V in the text box</span>
                </div>
              </div>
            )}

            <div style={{ marginTop: 6, paddingLeft: 28 }}>
              <button onClick={() => addStep(idx)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: 'var(--primary)', padding: 0 }}>
                + Add step below
              </button>
            </div>
          </div>
        ))}
      </div>
      {steps.length === 0 && (
        <Button variant="secondary" onClick={() => update([{ description: '' }])} style={{ marginTop: 8, fontSize: 12 }}>+ Add Step</Button>
      )}
    </div>
  );
}
